import {AxiosError} from "axios";
import {createContext, useCallback, useEffect, useMemo, useState} from 'react'
import {useNavigate} from "react-router-dom";
import {ApiConfig} from "../api/admin/ApiConfig";
import {AuthApi} from "../api/admin/generated";
import {useSpinnerStatusContext} from "../hook/UseSpinnerStatusContext";
import {Assert} from "../utils/Assert";
import {logger} from "../utils/Logger";
import {Utils} from "../utils/Utils";

const authApi = new AuthApi(ApiConfig.config())

export interface IAuthenticatedUserContext {
    authenticatedUser: string | undefined,
    clearAuthenticatedUser: () => void,
}

export const AuthenticatedUserContext = createContext<IAuthenticatedUserContext>({
    authenticatedUser: undefined,
    clearAuthenticatedUser: () => {},
});

interface AuthenticatedUserContextProviderProps {
    children: React.ReactNode;
}

/**
 * Provides the current authenticated user context to its children components.
 */
export function AuthenticatedUserContextProvider({children} : Readonly<AuthenticatedUserContextProviderProps>)
{
    const [authenticatedUser, setAuthenticatedUser] = useState<string | undefined>();
    /**
     * Keeps track of activities for which the spinner should be shown. If an activity is started, the counter should
     * be increased by one and after the activity
     */
    const {setSpinnerStateCounter} = useSpinnerStatusContext();
    /**
     * Navigate is used to navigate to the login page if the user is no longer authenticated
     */
    const navigate = useNavigate();

    function clearAuthenticatedUser() {
        setAuthenticatedUser(undefined);
    }

    const getAuthenticatedUser = useCallback(() =>
    {
        logger.debug("Checking if user is authenticated")

        if (setSpinnerStateCounter !== undefined) {
            setSpinnerStateCounter(count => count + 1);
        }

        authApi.authControllerGetAuthenticatedUser().then((response: { data: string; }) =>
        {
            logger.info(`User ${response.data} was authenticated`);

            setAuthenticatedUser(Assert.assertNotNullOrUndefined(response.data));
        }).catch((err: Error | AxiosError) => {
            if (err instanceof AxiosError)
            {
                // do not log 401 errors
                if (err.response?.status !== 401) {
                    logger.error("Error getting authenticated user", err);
                }
                else {
                    logger.info("User is not authenticated")
                }
            }

            clearAuthenticatedUser();

            Utils.redirectToLoginIfNotAuthenticated(err, navigate);
        }).finally(() => {
            if (setSpinnerStateCounter !== undefined) {
                setSpinnerStateCounter(count => count - 1);
            }
        })
    }, [setSpinnerStateCounter, navigate])

    useEffect(() => {
        getAuthenticatedUser();
    }, [getAuthenticatedUser]);

    const exposedData = useMemo<IAuthenticatedUserContext>(() => {
        const authenticatedUserContext : IAuthenticatedUserContext =
        {
            authenticatedUser : authenticatedUser,
            clearAuthenticatedUser : clearAuthenticatedUser
        }

        return authenticatedUserContext;
    }, [authenticatedUser]);

    return(
        <AuthenticatedUserContext.Provider value = {exposedData}>
            {children}
        </AuthenticatedUserContext.Provider>
    )
}